import React, { useRef, useState, useEffect } from "react";

const colorsArray = [
  { label: "Teal",   value: "#1D9E75" },
  { label: "Blue",   value: "#378ADD" },
  { label: "Purple", value: "#7F77DD" },
  { label: "Coral",  value: "#D85A30" },
  { label: "Pink",   value: "#D4537E" },
  { label: "Green",  value: "#639922" },
  { label: "Amber",  value: "#BA7517" },
  { label: "Red",    value: "#E24B4A" },
  { label: "Gray",   value: "#888780" },
  { label: "Black",   value: "#000000" },
];

const labelStyle = { fontSize: '11px', color: '#555', marginBottom: '2px' };

const ColorPickerField = ({ value = "#1D9E75", onChange, index }) => {
  const nativeRef = useRef(null);
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);

  const matched = colorsArray.find(
    (c) => c.value.toLowerCase() === (value || "").toLowerCase()
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape, handle keyboard nav
  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); }
    if (e.key === "Tab") { setOpen(false); } // let tab move naturally
  };

  const handleSelect = (colorValue) => {
    onChange(colorValue);
    setOpen(false);
  };

  const handleNativeChange = (e) => {
    onChange(e.target.value);
    setOpen(false);
  };

  const displayLabel = matched ? matched.label : `Custom`;
  const displayValue = value || "#000000";

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: '90px' }}>
      <label style={labelStyle}>Color</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

        {/* Native color swatch — tabIndex -1 so tab skips it */}
        <input
          ref={nativeRef}
          type="color"
          tabIndex={-1}
          value={displayValue}
          onChange={handleNativeChange}
          style={{
            width: '26px',
            height: '26px',
            border: '1px solid #ccc',
            borderRadius: '3px',
            padding: '1px',
            cursor: 'pointer',
            flexShrink: 0,
            background: 'none',
          }}
          title="Pick custom color"
        />

        {/* Custom dropdown trigger */}
        <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
          <button
            type="button"
            onKeyDown={handleKeyDown}
            onClick={() => setOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: '100%',
              padding: '4px 6px',
              fontSize: '13px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              background: '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              boxSizing: 'border-box',
              height: '26px',
            }}
            aria-haspopup="listbox"
            aria-expanded={open}
            id={`color-picker-btn-${index}`}
          >
            {/* Color box */}
            <span style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              borderRadius: '2px',
              background: displayValue,
              border: '1px solid rgba(0,0,0,0.15)',
              flexShrink: 0,
            }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
              {displayLabel} ({displayValue})
            </span>
            {/* Dropdown arrow */}
            <span style={{ fontSize: '10px', color: '#888', flexShrink: 0 }}>▾</span>
          </button>

          {open && (
            <ul
              role="listbox"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 9999,
                margin: '2px 0 0',
                padding: '2px 0',
                border: '1px solid #ccc',
                borderRadius: '3px',
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                listStyle: 'none',
                maxHeight: '220px',
                overflowY: 'auto',
              }}
            >
              {colorsArray.map((c) => (
                <li
                  key={c.value}
                  role="option"
                  aria-selected={value?.toLowerCase() === c.value.toLowerCase()}
                  onClick={() => handleSelect(c.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '5px 8px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    background: value?.toLowerCase() === c.value.toLowerCase() ? '#f0f0f0' : 'transparent',
                    color: '#333',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = value?.toLowerCase() === c.value.toLowerCase() ? '#f0f0f0' : 'transparent'}
                >
                  <span style={{
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    borderRadius: '2px',
                    background: c.value,
                    border: '1px solid rgba(0,0,0,0.15)',
                    flexShrink: 0,
                  }} />
                  <span>{c.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#999' }}>{c.value}</span>
                </li>
              ))}

              {/* Custom color option */}
              <li
                role="option"
                onClick={() => { 
                    setOpen(false);
                    if (nativeRef && nativeRef.current) {
                        nativeRef.current.click();
                    }
                 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '5px 8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  borderTop: '1px solid #eee',
                  color: '#555',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  borderRadius: '2px',
                  background: matched ? '#fff' : displayValue,
                  border: '1px solid rgba(0,0,0,0.15)',
                  flexShrink: 0,
                }} />
                <span>{matched ? 'Custom…' : `Custom (${displayValue})`}</span>
              </li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorPickerField;